import { useEffect } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Code2,
  Database,
  Server,
  Layers,
  GaugeCircle,
  GraduationCap,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  Clock,
} from "lucide-react";

// 👉 Replace these with your local images if you prefer
const IMAGES = {
  hero: "/assets/php-programming-scaled.jpg",
  classroom:
    "/assets/photo-1517048676732-d65bc937f952.AVIF",
  code: "/assets/photo-1515879218367-8466d910aaa4.AVIF",
};

export default function PHP() {
  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 80, easing: "ease-out" });
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 overflow-hidden ">
      {/* Hero */}
      <section className="relative h-[70vh] w-full grid place-items-center overflow-hidden">
        <img
          src={IMAGES.hero}
          alt="PHP Course"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/70 via-slate-900/50 to-slate-900/80" />
        <div className="relative z-10 max-w-6xl px-6 text-center text-white">
          <div
            data-aos="fade-up"
            className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1 text-sm backdrop-blur"
          >
            {/* <Sparkles className="h-4 w-4" /> New Batch Enrolling Now */}
          </div>
          <h1
            data-aos="fade-up"
            data-aos-delay="100"
            className="mt-5 text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight"
          >
            PHP & MySQL Full-Stack Developer
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            className="mt-4 mx-auto max-w-3xl text-white/85"
          >
            Build modern, secure, database‑driven web apps using PHP 8, MySQL,
            and industry best practices. From fundamentals to deployment.
          </p>
          <div
            data-aos="fade-up"
            data-aos-delay="300"
            className="mt-8 flex flex-wrap items-center justify-center gap-4"
          >
            <Link
              to="/contact"
              className="rounded-2xl bg-indigo-500 px-6 py-3 font-semibold text-white shadow-lg shadow-indigo-500/30 transition hover:scale-[1.02] hover:bg-indigo-600 animate-bounce"
            >
              Enrol Now
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-white py-8 shadow-sm">
        <div className="mx-auto grid max-w-6xl grid-cols-2 gap-6 px-6 sm:grid-cols-4">
          {[
            { label: "Hours", value: "120+" },
            { label: "Projects", value: "6" },
            { label: "Placement", value: "Assistance" },
            { label: "Level", value: "Beginner → Pro" },
          ].map((s, i) => (
            <div
              key={s.label}
              data-aos="zoom-in"
              data-aos-delay={i * 100}
              className="text-center"
            >
              <div className="text-3xl font-extrabold text-slate-900">
                {s.value}
              </div>
              <div className="mt-1 text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What you'll learn */}
      <section className="mx-auto max-w-6xl px-6 py-14">
        <h2
          className="text-3xl font-bold text-slate-900 mb-6"
          data-aos="fade-up"
        >
          What you'll learn
        </h2>
        <ul className="space-y-3">
          {[
            {
              icon: Code2,
              text: "PHP 8 syntax, OOP, Composer & PSR standards",
              color: "bg-indigo-100 text-indigo-700",
            },
            {
              icon: Database,
              text: "MySQL schema design, joins, indexes, PDO",
              color: "bg-green-100 text-green-700",
            },
            {
              icon: ShieldCheck,
              text: "Security: XSS, CSRF, SQLi, password hashing",
              color: "bg-red-100 text-red-700",
            },
            {
              icon: Server,
              text: "APIs, MVC pattern, Laravel fundamentals",
              color: "bg-yellow-100 text-yellow-700",
            },
            {
              icon: Layers,
              text: "Templating, Blade, and modern UI workflows",
              color: "bg-purple-100 text-purple-700",
            },
          ].map((item, i) => (
            <li
              key={i}
              className={`flex items-start gap-3 rounded-2xl p-4 shadow-lg ring-1 ring-slate-200 transition hover:scale-105 hover:shadow-2xl animate-fadeInUp ${item.color}`}
              data-aos="fade-up"
              data-aos-delay={100 + i * 75}
            >
              <item.icon className="mt-0.5 h-6 w-6 animate-bounce" />
              <span className="font-medium">{item.text}</span>
            </li>
          ))}
        </ul>
      </section>

      <section id="syllabus" className="bg-white py-14">
        <div className="mx-auto max-w-6xl px-6">
          <h2
            className="text-3xl font-bold text-slate-900 mb-4"
            data-aos="fade-up"
          >
            Syllabus Overview
          </h2>
          <p
            className="text-slate-600 mb-8"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            Carefully designed modules with progressive difficulty and hands‑on
            practice.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {[
              {
                title: "Module 1: PHP Foundations",
                items: [
                  "Variables, types, control flow",
                  "Functions, arrays, superglobals",
                  "OOP: classes, traits, namespaces",
                ],
                color: "bg-indigo-50 border-indigo-500",
              },
              {
                title: "Module 2: Working With Data",
                items: [
                  "MySQL basics, schema & relations",
                  "CRUD with PDO & prepared statements",
                  "Migrations & seeders",
                ],
                color: "bg-green-50 border-green-500",
              },
              {
                title: "Module 3: Web & Security",
                items: [
                  "Forms, sessions, cookies",
                  "Authentication & authorization",
                  "Sanitization, CSRF, XSS, SQLi",
                ],
                color: "bg-red-50 border-red-500",
              },
              {
                title: "Module 4: Frameworks & APIs",
                items: [
                  "Intro to Laravel & MVC",
                  "Blade templating & Eloquent ORM",
                  "REST APIs & Postman",
                ],
                color: "bg-yellow-50 border-yellow-500",
              },
            ].map((card, i) => (
              <details
                key={card.title}
                open
                className={`group rounded-2xl border-l-8 p-4 transition hover:scale-105 hover:shadow-xl ${card.color}`}
                data-aos="fade-up"
                data-aos-delay={i * 120}
              >
                <summary className="flex cursor-pointer items-center justify-between text-lg font-semibold">
                  {card.title}{" "}
                  <GaugeCircle className="h-5 w-5 transition group-open:rotate-180" />
                </summary>
                <ul className="mt-3 space-y-2 pl-1 text-slate-700">
                  {card.items.map((it) => (
                    <li key={it} className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-slate-600 animate-pulse" />{" "}
                      {it}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 py-14">
        <h2
          className="text-3xl font-bold text-slate-900 mb-4"
          data-aos="fade-up"
        >
          Build Real Projects
        </h2>
        <p
          className="text-slate-600 mb-8"
          data-aos="fade-up"
          data-aos-delay="100"
        >
          Portfolio‑ready apps you'll create during the course.
        </p>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              title: "Blog CMS",
              desc: "Admin + public site with CRUD, image upload, slugs, and search.",
              color: "bg-indigo-50 border-indigo-400",
              img: "/assets/blogadmin_0.png",
            },
            {
              title: "E-Commerce Mini",
              desc: "Products, cart, orders, auth, and payment integration demo.",
              color: "bg-green-50 border-green-400",
              img: "/assets/maxresdefault.jpg",
            },
            {
              title: "REST API",
              desc: "JWT-secured JSON API for a mobile client with rate limiting.",
              color: "bg-yellow-50 border-yellow-400",
              img: "/assets/dd640ba2-f382-43d1-a8ad-2c313b87e18f.png",
            },
          ].map((p, i) => (
            <article
              key={p.title}
              data-aos="zoom-in"
              data-aos-delay={i * 120}
              className={`group rounded-3xl p-6 shadow-md ring-1 ring-slate-200 transition hover:-translate-y-1 hover:shadow-xl ${p.color}`}
            >
              <img
                src={p.img}
                alt={p.title}
                className="h-36 w-full rounded-2xl object-cover shadow-sm"
              />
              <h3 className="mt-4 text-lg font-semibold flex items-center gap-2">
                {p.title}{" "}
                <Sparkles className="h-5 w-5 animate-bounce text-indigo-500" />
              </h3>
              <p className="mt-1 text-sm text-slate-600">{p.desc}</p>
            </article>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 p-4">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-indigo-50 via-slate-50 to-white" />
        <div className="mx-auto max-w-6xl rounded-3xl bg-indigo-600 px-6 py-12 text-white shadow-xl">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div data-aos="fade-right">
              <h3 className="text-2xl font-bold">Kickstart your PHP career</h3>
              <p className="mt-2 text-indigo-50">
                Get guidance from mentors, resume prep, and mock interviews.
              </p>
            </div>
            <div
              data-aos="fade-left"
              className="flex flex-col items-start gap-3"
            >
              <Link
                to="/contact"
                className="rounded-2xl bg-white px-8 py-3 font-semibold text-indigo-700 shadow-lg transition hover:scale-[1.02]"
              >
                Enroll Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer mini */}
      {/* <footer className="border-t bg-white py-8">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-6 text-sm text-slate-500">
          <div className="flex items-center gap-2">
            <Code2 className="h-4 w-4" /> PHP by CSG Education
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hover:text-slate-800">
              Home
            </Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-slate-800">
              Contact
            </Link>
          </div>
        </div>
      </footer> */}
    </div>
  );
}
